import type { GameType } from '../engine/rules';

export const LANGS = ['en', 'it', 'es', 'fr', 'pt'] as const;
export type Lang = (typeof LANGS)[number];
export const LANG_NAMES: Record<Lang, string> = { en: 'English', it: 'Italiano', es: 'Español', fr: 'Français', pt: 'Português' };

const en = {
  tagline: 'The classic Italian card game',
  briscolaTagline: 'The classic Italian trump & trick game',
  gameMode: 'Game',
  playPc: 'Play vs. PC',
  playFriend: 'Play with a Friend',
  settings: 'Settings',
  howToPlay: 'How to play',
  back: 'Back',
  start: 'Start',
  difficulty: 'Difficulty',
  EASY: 'Beginner',
  MEDIUM: 'Expert',
  HARD: 'Master',
  EASY_desc: 'Relaxed. Makes mistakes, never counts cards.',
  MEDIUM_desc: 'Thinks about every capture and trails carefully.',
  HARD_desc: 'Counts every card and plays the endgame perfectly.',
  EASY_briscola_desc: 'Plays casually and often wastes trumps.',
  MEDIUM_briscola_desc: 'Protects Aces & Threes and trumps wisely.',
  HARD_briscola_desc: 'Counts every card and solves the final tricks perfectly.',
  targetScore: 'Target score',
  matchFormat: 'Match format',
  standard: 'Standard',
  long: 'Long',
  singleGame: 'Single',
  bestOf3: 'Best of 3',
  bestOf5: 'Best of 5',
  turnTimer: 'Turn timer',
  none: 'None',
  pileInspector: 'Pile inspector',
  pileInspectorDesc: 'Let players peek at their own captures (casual).',
  createLink: 'Create Invite Link',
  inviteTitle: 'Invite a friend',
  inviteText: 'Send this link. The match starts as soon as they open it.',
  share: 'Share',
  copy: 'Copy link',
  copied: 'Copied!',
  waitingFor: 'Waiting for your friend…',
  joinTitle: "You're invited to Scopa",
  joinTitleBriscola: "You're invited to Briscola",
  joinAs: 'Your name',
  join: 'Join match',
  roomNotFound: 'This match link has expired or does not exist.',
  roomFull: 'This match already has two players.',
  connecting: 'Connecting…',
  reconnecting: 'Reconnecting…',
  offline: 'Server unreachable. Check your connection.',
  you: 'You',
  computer: 'Computer',
  deck: 'Deck',
  pile: 'Pile',
  cards: 'cards',
  yourTurn: 'Your turn',
  theirTurn: '{name} is thinking…',
  chooseCapture: 'Choose which cards to capture',
  cancel: 'Cancel',
  scopa: 'Scopa!',
  lastTake: 'Last capture takes the table',
  settebelloYours: 'The 7 of Coins is yours! +1',
  settebelloTheirs: '{name} grabbed the 7 of Coins!',
  roundOver: 'Round {n}',
  carte: 'Cards',
  denari: 'Coins',
  settebello: 'Settebello',
  primiera: 'Primiera',
  scope: 'Scope',
  trump: 'Briscola',
  suit_DENARI: 'Coins',
  suit_COPPE: 'Cups',
  suit_SPADE: 'Swords',
  suit_BASTONI: 'Clubs',
  assi: 'Aces (11)',
  tre: 'Threes (10)',
  figure: 'Royals (4/3/2)',
  puntiMano: 'Hand points',
  handsWon: 'Hands won',
  drawRound: 'Draw (60–60)!',
  total: 'Total',
  continue: 'Continue',
  waitingOpp: 'Waiting for opponent…',
  youWin: 'You win!',
  youLose: '{name} wins',
  forfeitWin: '{name} left the match — you win.',
  forfeitLose: 'You forfeited the match.',
  rematch: 'Rematch',
  rematchWaiting: 'Waiting for rematch…',
  oppWantsRematch: '{name} wants a rematch!',
  home: 'Home',
  quit: 'Leave match',
  quitConfirm: 'Leave this match?',
  paused: '{name} disconnected. Waiting {s}s…',
  afk: 'AFK',
  timedOut: 'Time up — a card was played automatically',
  language: 'Language',
  deckStyle: 'Card deck',
  showNumbers: 'Show card numbers',
  sound: 'Sound effects',
  music: 'Music',
  haptics: 'Vibration',
  emotesMuted: 'Mute emotes',
  name: 'Your name',
  locked: 'Locked',
  unlockBergamasche: 'Win 3 matches to unlock',
  unlockTrevisane: 'Beat the Master to unlock',
  unlocked: 'New deck unlocked: {deck}!',
  stats: 'Stats',
  wins: 'Wins',
  losses: 'Losses',
  emote_scopa: 'Scopa!',
  emote_luck: 'Che fortuna!',
  emote_mamma: 'Mamma mia!',
  emote_nice: 'Bella giocata!',
  emote_hello: 'Ciao!',
  emote_gg: 'Good game!',
  myPile: 'My captures',
  coinsCount: 'Coins',
  yes: 'Yes',
  no: 'No',
  rotate: 'Please rotate your device to portrait',
  points: 'pts',
  to: 'to',
  nextTrack: 'Next song',
  watchIntro: 'Watch Intro Video',
  introVideoDesc: 'Quick gameplay & rules showcase',
  close: 'Close',
  newRound: 'Round {n} — {name} deals',
  rules: [
    'Scopa is played with a 40-card Italian deck: Coins, Cups, Swords and Clubs, Ace to 7 plus Fante (8), Cavallo (9) and Re (10).',
    'Each player gets 3 cards; 4 go face-up on the table. When both hands are empty, 3 more each are dealt until the deck runs out.',
    'On your turn play one card. If it matches a table card of the same value you must take it. Otherwise, if a group of cards adds up to it, you must take one such group.',
    'If your card matches a single card, you must take the single card rather than a sum.',
    "If nothing can be taken, your card stays on the table.",
    'Clearing the whole table is a Scopa: +1 point. (Not on the very last play of the round.)',
    'At the end, cards left on the table go to whoever captured last.',
    'Each round scores: most cards (+1), most Coins (+1), the 7 of Coins "Settebello" (+1), best Primiera (+1), and +1 per Scopa.',
    'Primiera: your best card in each suit, valued 7=21, 6=18, Ace=16, 5=15, 4=14, 3=13, 2=12, faces=10. Missing a suit loses it.',
    'First to the target score with a lead wins.',
  ],
  briscolaRules: [
    'Briscola is played with a 40-card Italian deck: Coins, Cups, Swords and Clubs.',
    'Each player receives 3 cards. One card is turned face-up under the deck: its suit is the Briscola (trump) for the hand, and it is the last card drawn.',
    'The non-dealer leads the first trick by playing any card. The second player may play any card — there is no obligation to follow suit or play a trump.',
    'Within the same suit, card strength from highest to lowest is: Ace (1), Three (3), Re (10), Cavallo (9), Fante (8), 7, 6, 5, 4, 2.',
    'If the second player plays a Briscola (trump) against a non-trump lead, the Briscola wins. If both are trumps or both are the led suit, the stronger rank wins; otherwise the lead card holds.',
    'The winner of each trick collects both cards, draws first from the deck (followed by the other player), and leads the next trick.',
    'Card values total 120 points across the deck: Ace = 11 pts, Three = 10 pts, Re (10) = 4 pts, Cavallo (9) = 3 pts, Fante (8) = 2 pts. All other cards (7, 6, 5, 4, 2) are worth 0 pts.',
    'Scoring 61 or more points wins the hand (60–60 is a draw).',
  ],
};

export type Strings = typeof en;
type Partial = { [K in keyof Strings]?: Strings[K] };

const it: Partial = {
  tagline: 'Il classico gioco di carte italiano',
  briscolaTagline: 'Il classico gioco di briscola e prese',
  gameMode: 'Gioco',
  playPc: 'Gioca contro il PC',
  playFriend: 'Gioca con un amico',
  settings: 'Impostazioni',
  howToPlay: 'Come si gioca',
  back: 'Indietro',
  start: 'Inizia',
  difficulty: 'Difficoltà',
  EASY: 'Principiante',
  MEDIUM: 'Esperto',
  HARD: 'Maestro',
  EASY_desc: 'Rilassato. Sbaglia e non conta le carte.',
  MEDIUM_desc: 'Valuta ogni presa e scarta con attenzione.',
  HARD_desc: 'Conta tutte le carte e gioca il finale alla perfezione.',
  EASY_briscola_desc: 'Gioca con leggerezza e spesso spreca le briscole.',
  MEDIUM_briscola_desc: 'Protegge i carichi e usa i tagli con astuzia.',
  HARD_briscola_desc: 'Conta ogni carta e risolve le ultime prese alla perfezione.',
  targetScore: 'Punteggio',
  matchFormat: 'Formato partita',
  standard: 'Standard',
  long: 'Lunga',
  singleGame: 'Singola',
  bestOf3: 'Al meglio di 3',
  bestOf5: 'Al meglio di 5',
  turnTimer: 'Tempo per turno',
  none: 'Nessuno',
  pileInspector: 'Mostra prese',
  pileInspectorDesc: 'I giocatori possono vedere le proprie prese (partita amichevole).',
  createLink: 'Crea link di invito',
  inviteTitle: 'Invita un amico',
  inviteText: 'Invia questo link. La partita inizia appena lo apre.',
  share: 'Condividi',
  copy: 'Copia link',
  copied: 'Copiato!',
  waitingFor: 'In attesa del tuo amico…',
  joinTitle: 'Sei invitato a Scopa',
  joinTitleBriscola: 'Sei invitato a Briscola',
  joinAs: 'Il tuo nome',
  join: 'Entra',
  roomNotFound: 'Questo link è scaduto o non esiste.',
  roomFull: 'Questa partita ha già due giocatori.',
  connecting: 'Connessione…',
  reconnecting: 'Riconnessione…',
  offline: 'Server non raggiungibile. Controlla la connessione.',
  you: 'Tu',
  computer: 'Computer',
  deck: 'Mazzo',
  pile: 'Prese',
  cards: 'carte',
  yourTurn: 'Tocca a te',
  theirTurn: '{name} sta pensando…',
  chooseCapture: 'Scegli le carte da prendere',
  cancel: 'Annulla',
  scopa: 'Scopa!',
  lastTake: "L'ultima presa prende il tavolo",
  settebelloYours: 'Il sette bello è tuo! +1',
  settebelloTheirs: '{name} ha preso il sette bello!',
  roundOver: 'Mano {n}',
  carte: 'Carte',
  denari: 'Denari',
  settebello: 'Settebello',
  primiera: 'Primiera',
  scope: 'Scope',
  trump: 'Briscola',
  suit_DENARI: 'Denari',
  suit_COPPE: 'Coppe',
  suit_SPADE: 'Spade',
  suit_BASTONI: 'Bastoni',
  assi: 'Assi (11)',
  tre: 'Tre (10)',
  figure: 'Figure (4/3/2)',
  puntiMano: 'Punti mano',
  handsWon: 'Mani vinte',
  drawRound: 'Pari (60–60)!',
  total: 'Totale',
  continue: 'Continua',
  waitingOpp: "In attesa dell'avversario…",
  youWin: 'Hai vinto!',
  youLose: 'Vince {name}',
  forfeitWin: '{name} ha abbandonato — hai vinto.',
  forfeitLose: 'Hai perso a tavolino.',
  rematch: 'Rivincita',
  rematchWaiting: 'In attesa della rivincita…',
  oppWantsRematch: '{name} vuole la rivincita!',
  home: 'Menu',
  quit: 'Esci dalla partita',
  quitConfirm: 'Uscire dalla partita?',
  paused: '{name} si è disconnesso. Attendo {s}s…',
  timedOut: 'Tempo scaduto — carta giocata automaticamente',
  language: 'Lingua',
  deckStyle: 'Mazzo di carte',
  showNumbers: 'Mostra i numeri',
  sound: 'Effetti sonori',
  music: 'Musica',
  haptics: 'Vibrazione',
  emotesMuted: 'Silenzia emote',
  name: 'Il tuo nome',
  locked: 'Bloccato',
  unlockBergamasche: 'Vinci 3 partite per sbloccarlo',
  unlockTrevisane: 'Batti il Maestro per sbloccarlo',
  unlocked: 'Nuovo mazzo sbloccato: {deck}!',
  stats: 'Statistiche',
  wins: 'Vittorie',
  losses: 'Sconfitte',
  emote_hello: 'Ciao!',
  emote_gg: 'Bella partita!',
  myPile: 'Le mie prese',
  coinsCount: 'Denari',
  yes: 'Sì',
  no: 'No',
  rotate: 'Ruota il dispositivo in verticale',
  points: 'pt',
  to: 'a',
  nextTrack: 'Prossima canzone',
  watchIntro: 'Guarda il video introduttivo',
  introVideoDesc: 'Presentazione rapida del gioco e delle regole',
  close: 'Chiudi',
  newRound: 'Mano {n} — distribuisce {name}',
  rules: [
    'Si gioca con un mazzo italiano da 40 carte: Denari, Coppe, Spade e Bastoni, dall’Asso al 7 più Fante (8), Cavallo (9) e Re (10).',
    'Ogni giocatore riceve 3 carte; 4 vanno scoperte in tavola. Quando le mani sono vuote se ne danno altre 3 finché il mazzo finisce.',
    'Al tuo turno giochi una carta. Se in tavola c’è una carta dello stesso valore devi prenderla, altrimenti devi prendere un gruppo di carte la cui somma è uguale, se c’è.',
    'Se c’è una carta singola uguale, devi prendere quella e non la somma.',
    'Se non puoi prendere nulla, la carta resta in tavola.',
    'Se prendi tutte le carte in tavola fai Scopa: +1 punto (non all’ultima giocata della mano).',
    'Alla fine le carte rimaste vanno a chi ha fatto l’ultima presa.',
    'Ogni mano assegna: carte (+1), denari (+1), Settebello (+1), primiera (+1) e +1 per ogni Scopa.',
    'Primiera: la carta migliore per seme, 7=21, 6=18, Asso=16, 5=15, 4=14, 3=13, 2=12, figure=10. Senza un seme la perdi.',
    'Vince chi raggiunge il punteggio con almeno un punto di vantaggio.',
  ],
  briscolaRules: [
    'La Briscola si gioca con un mazzo italiano da 40 carte: Denari, Coppe, Spade e Bastoni.',
    'Ogni giocatore riceve 3 carte. Una carta viene scoperta sotto il mazzo: il suo seme è la Briscola della mano ed è l’ultima carta a essere pescata.',
    'Il giocatore di mano apre giocando una carta qualsiasi. Il secondo può rispondere con qualsiasi carta: non c’è obbligo di rispondere al seme né di tagliare.',
    'All’interno dello stesso seme la forza delle carte, dalla più alta alla più bassa, è: Asso (1), Tre (3), Re (10), Cavallo (9), Fante (8), 7, 6, 5, 4, 2.',
    'Se il secondo gioca una Briscola su un seme diverso, vince la presa. Se entrambi giocano lo stesso seme (o entrambi Briscola) vince la carta più forte; altrimenti tiene la prima carta.',
    'Chi vince la presa raccoglie le due carte, pesca per primo dal mazzo (seguito dall’avversario) e apre la presa successiva.',
    'Nel mazzo ci sono 120 punti totali: Asso = 11 pt, Tre = 10 pt, Re (10) = 4 pt, Cavallo (9) = 3 pt, Fante (8) = 2 pt. Le altre carte ("lisci": 7, 6, 5, 4, 2) valgono 0 pt.',
    'Vince la mano chi totalizza almeno 61 punti (60–60 è patta).',
  ],
};

const es: Partial = {
  tagline: 'El clásico juego de cartas italiano',
  briscolaTagline: 'El clásico juego italiano de triunfo y bazas',
  gameMode: 'Juego',
  playPc: 'Jugar contra el PC',
  playFriend: 'Jugar con un amigo',
  settings: 'Ajustes',
  howToPlay: 'Cómo jugar',
  back: 'Atrás',
  start: 'Empezar',
  difficulty: 'Dificultad',
  EASY: 'Principiante',
  MEDIUM: 'Experto',
  HARD: 'Maestro',
  EASY_desc: 'Relajado. Comete errores y no cuenta cartas.',
  MEDIUM_desc: 'Piensa cada captura y descarta con cuidado.',
  HARD_desc: 'Cuenta todas las cartas y juega el final a la perfección.',
  EASY_briscola_desc: 'Juega relajado y suele malgastar triunfos.',
  MEDIUM_briscola_desc: 'Protege Ases y Treses y falla con astucia.',
  HARD_briscola_desc: 'Cuenta cada carta y resuelve las últimas bazas a la perfección.',
  targetScore: 'Puntuación objetivo',
  matchFormat: 'Formato de partida',
  standard: 'Estándar',
  long: 'Larga',
  singleGame: 'Única',
  bestOf3: 'Al mejor de 3',
  bestOf5: 'Al mejor de 5',
  turnTimer: 'Tiempo por turno',
  none: 'Ninguno',
  pileInspector: 'Ver capturas',
  pileInspectorDesc: 'Permite ver tus propias capturas (casual).',
  createLink: 'Crear enlace de invitación',
  inviteTitle: 'Invita a un amigo',
  inviteText: 'Envía este enlace. La partida empieza en cuanto lo abra.',
  share: 'Compartir',
  copy: 'Copiar enlace',
  copied: '¡Copiado!',
  waitingFor: 'Esperando a tu amigo…',
  joinTitle: 'Te han invitado a Scopa',
  joinTitleBriscola: 'Te han invitado a Briscola',
  joinAs: 'Tu nombre',
  join: 'Unirse',
  roomNotFound: 'Este enlace ha caducado o no existe.',
  roomFull: 'Esta partida ya tiene dos jugadores.',
  connecting: 'Conectando…',
  reconnecting: 'Reconectando…',
  offline: 'Servidor no disponible. Revisa tu conexión.',
  you: 'Tú',
  computer: 'Ordenador',
  deck: 'Mazo',
  pile: 'Capturas',
  cards: 'cartas',
  yourTurn: 'Tu turno',
  theirTurn: '{name} está pensando…',
  chooseCapture: 'Elige qué cartas capturar',
  cancel: 'Cancelar',
  lastTake: 'La última captura se lleva la mesa',
  settebelloYours: '¡El 7 de oros es tuyo! +1',
  settebelloTheirs: '¡{name} se llevó el 7 de oros!',
  roundOver: 'Ronda {n}',
  carte: 'Cartas',
  denari: 'Oros',
  trump: 'Triunfo',
  suit_DENARI: 'Oros',
  suit_COPPE: 'Copas',
  suit_SPADE: 'Espadas',
  suit_BASTONI: 'Bastos',
  assi: 'Ases (11)',
  tre: 'Treses (10)',
  figure: 'Figuras (4/3/2)',
  puntiMano: 'Puntos de mano',
  handsWon: 'Manos ganadas',
  drawRound: '¡Empate (60–60)!',
  total: 'Total',
  continue: 'Continuar',
  waitingOpp: 'Esperando al rival…',
  youWin: '¡Has ganado!',
  youLose: 'Gana {name}',
  forfeitWin: '{name} abandonó — ganas tú.',
  forfeitLose: 'Has perdido por abandono.',
  rematch: 'Revancha',
  rematchWaiting: 'Esperando la revancha…',
  oppWantsRematch: '¡{name} quiere la revancha!',
  home: 'Inicio',
  quit: 'Salir de la partida',
  quitConfirm: '¿Salir de la partida?',
  paused: '{name} se desconectó. Esperando {s}s…',
  timedOut: 'Tiempo agotado — se jugó una carta automáticamente',
  language: 'Idioma',
  deckStyle: 'Baraja',
  showNumbers: 'Mostrar números',
  sound: 'Efectos de sonido',
  music: 'Música',
  haptics: 'Vibración',
  emotesMuted: 'Silenciar emotes',
  name: 'Tu nombre',
  locked: 'Bloqueado',
  unlockBergamasche: 'Gana 3 partidas para desbloquear',
  unlockTrevisane: 'Vence al Maestro para desbloquear',
  unlocked: '¡Nueva baraja desbloqueada: {deck}!',
  stats: 'Estadísticas',
  wins: 'Victorias',
  losses: 'Derrotas',
  emote_hello: '¡Hola!',
  emote_gg: '¡Buena partida!',
  myPile: 'Mis capturas',
  coinsCount: 'Oros',
  yes: 'Sí',
  no: 'No',
  rotate: 'Gira el dispositivo en vertical',
  nextTrack: 'Siguiente canción',
  watchIntro: 'Ver video de introducción',
  introVideoDesc: 'Demostración rápida del juego y las reglas',
  close: 'Cerrar',
  newRound: 'Ronda {n} — reparte {name}',
  rules: [
    'Se juega con una baraja italiana de 40 cartas: Oros, Copas, Espadas y Bastos, del As al 7 más Sota (8), Caballo (9) y Rey (10).',
    'Cada jugador recibe 3 cartas y 4 van boca arriba a la mesa. Cuando ambas manos se vacían se reparten 3 más hasta acabar el mazo.',
    'En tu turno juegas una carta. Si hay una carta del mismo valor debes capturarla; si no, debes capturar cartas cuya suma sea igual, si las hay.',
    'Si hay una carta individual igual, debes capturar esa y no una suma.',
    'Si no puedes capturar, tu carta queda en la mesa.',
    'Vaciar la mesa es Scopa: +1 punto (no en la última jugada de la ronda).',
    'Al final, las cartas que quedan son para quien capturó por última vez.',
    'Cada ronda puntúa: más cartas (+1), más oros (+1), el 7 de oros "Settebello" (+1), mejor Primiera (+1) y +1 por Scopa.',
    'Primiera: tu mejor carta de cada palo, 7=21, 6=18, As=16, 5=15, 4=14, 3=13, 2=12, figuras=10. Sin un palo la pierdes.',
    'Gana quien llega al objetivo con ventaja.',
  ],
  briscolaRules: [
    'La Briscola se juega con una baraja italiana de 40 cartas: Oros, Copas, Espadas y Bastos.',
    'Cada jugador recibe 3 cartas. Una carta queda boca arriba bajo el mazo: su palo marca la Briscola (triunfo) de la mano y es la última carta que se roba.',
    'El mano abre jugando cualquier carta. El segundo puede jugar cualquier carta: no es obligatorio asistir al palo ni fallar con triunfo.',
    'Dentro del mismo palo, la fuerza de mayor a menor es: As (1), Tres (3), Rey (10), Caballo (9), Sota (8), 7, 6, 5, 4, 2.',
    'Si el segundo juega Briscola contra otro palo, gana la baza. Si ambos juegan el mismo palo (o ambos Briscola), gana la carta más fuerte; si no, gana la primera carta.',
    'Quien gana la baza recoge ambas cartas, roba primero del mazo y sale en la siguiente baza.',
    'El mazo suma 120 puntos: As = 11 pt, Tres = 10 pt, Rey (10) = 4 pt, Caballo (9) = 3 pt, Sota (8) = 2 pt. Las demás cartas (7, 6, 5, 4, 2) valen 0 pt.',
    'Gana la mano quien sume 61 puntos o más (60–60 es empate).',
  ],
};

const fr: Partial = {
  tagline: 'Le jeu de cartes italien classique',
  briscolaTagline: "Le jeu italien classique d'atout et de plis",
  gameMode: 'Jeu',
  playPc: "Jouer contre l'ordinateur",
  playFriend: 'Jouer avec un ami',
  settings: 'Réglages',
  howToPlay: 'Règles du jeu',
  back: 'Retour',
  start: 'Commencer',
  difficulty: 'Difficulté',
  EASY: 'Débutant',
  MEDIUM: 'Expert',
  HARD: 'Maître',
  EASY_desc: 'Détendu. Fait des erreurs, ne compte pas les cartes.',
  MEDIUM_desc: 'Réfléchit à chaque prise et défausse prudemment.',
  HARD_desc: 'Compte chaque carte et joue la fin parfaitement.',
  EASY_briscola_desc: 'Joue de façon détendue et gaspille souvent ses atouts.',
  MEDIUM_briscola_desc: 'Protège les As et les Trois et coupe avec finesse.',
  HARD_briscola_desc: 'Compte chaque carte et résout les derniers plis à la perfection.',
  targetScore: 'Score à atteindre',
  matchFormat: 'Format du match',
  standard: 'Standard',
  long: 'Longue',
  singleGame: 'Unique',
  bestOf3: 'Au meilleur de 3',
  bestOf5: 'Au meilleur de 5',
  turnTimer: 'Temps par tour',
  none: 'Aucun',
  pileInspector: 'Voir ses plis',
  pileInspectorDesc: 'Les joueurs peuvent consulter leurs prises (partie amicale).',
  createLink: "Créer un lien d'invitation",
  inviteTitle: 'Inviter un ami',
  inviteText: "Envoie ce lien. La partie commence dès qu'il l'ouvre.",
  share: 'Partager',
  copy: 'Copier le lien',
  copied: 'Copié !',
  waitingFor: 'En attente de ton ami…',
  joinTitle: 'Tu es invité à une Scopa',
  joinTitleBriscola: 'Tu es invité à une Briscola',
  joinAs: 'Ton nom',
  join: 'Rejoindre',
  roomNotFound: "Ce lien a expiré ou n'existe pas.",
  roomFull: 'Cette partie a déjà deux joueurs.',
  connecting: 'Connexion…',
  reconnecting: 'Reconnexion…',
  offline: 'Serveur injoignable. Vérifie ta connexion.',
  you: 'Toi',
  computer: 'Ordinateur',
  deck: 'Pioche',
  pile: 'Plis',
  cards: 'cartes',
  yourTurn: 'À toi',
  theirTurn: '{name} réfléchit…',
  chooseCapture: 'Choisis les cartes à prendre',
  cancel: 'Annuler',
  lastTake: 'La dernière prise ramasse la table',
  settebelloYours: 'Le 7 de deniers est à toi ! +1',
  settebelloTheirs: '{name} a raflé le 7 de deniers !',
  roundOver: 'Manche {n}',
  carte: 'Cartes',
  denari: 'Deniers',
  trump: 'Atout',
  suit_DENARI: 'Deniers',
  suit_COPPE: 'Coupes',
  suit_SPADE: 'Épées',
  suit_BASTONI: 'Bâtons',
  assi: 'As (11)',
  tre: 'Trois (10)',
  figure: 'Figures (4/3/2)',
  puntiMano: 'Points de manche',
  handsWon: 'Manches gagnées',
  drawRound: 'Égalité (60–60) !',
  total: 'Total',
  continue: 'Continuer',
  waitingOpp: "En attente de l'adversaire…",
  youWin: 'Tu as gagné !',
  youLose: '{name} gagne',
  forfeitWin: '{name} a quitté — tu gagnes.',
  forfeitLose: 'Tu as perdu par forfait.',
  rematch: 'Revanche',
  rematchWaiting: 'En attente de la revanche…',
  oppWantsRematch: '{name} veut une revanche !',
  home: 'Accueil',
  quit: 'Quitter la partie',
  quitConfirm: 'Quitter la partie ?',
  paused: "{name} s'est déconnecté. Attente {s}s…",
  timedOut: 'Temps écoulé — une carte a été jouée automatiquement',
  language: 'Langue',
  deckStyle: 'Jeu de cartes',
  showNumbers: 'Afficher les chiffres',
  sound: 'Effets sonores',
  music: 'Musique',
  haptics: 'Vibration',
  emotesMuted: 'Couper les émotes',
  name: 'Ton nom',
  locked: 'Verrouillé',
  unlockBergamasche: 'Gagne 3 parties pour débloquer',
  unlockTrevisane: 'Bats le Maître pour débloquer',
  unlocked: 'Nouveau jeu débloqué : {deck} !',
  stats: 'Statistiques',
  wins: 'Victoires',
  losses: 'Défaites',
  emote_hello: 'Salut !',
  emote_gg: 'Bien joué !',
  myPile: 'Mes prises',
  coinsCount: 'Deniers',
  yes: 'Oui',
  no: 'Non',
  rotate: "Tourne l'appareil en portrait",
  to: 'à',
  nextTrack: 'Chanson suivante',
  watchIntro: "Regarder la vidéo d'intro",
  introVideoDesc: 'Présentation rapide du jeu et des règles',
  close: 'Fermer',
  newRound: 'Manche {n} — {name} distribue',
  rules: [
    'On joue avec un jeu italien de 40 cartes : Deniers, Coupes, Épées et Bâtons, de l’As au 7 plus Valet (8), Cavalier (9) et Roi (10).',
    'Chacun reçoit 3 cartes ; 4 sont posées face visible. Quand les mains sont vides, on redonne 3 cartes jusqu’à épuisement.',
    'À ton tour, joue une carte. Si une carte de même valeur est sur la table, tu dois la prendre ; sinon tu dois prendre des cartes dont la somme est égale, s’il y en a.',
    'Si une carte seule correspond, tu dois prendre celle-ci plutôt qu’une somme.',
    'Si tu ne peux rien prendre, ta carte reste sur la table.',
    'Vider la table, c’est une Scopa : +1 point (sauf au tout dernier coup).',
    'À la fin, les cartes restantes vont au dernier à avoir pris.',
    'Chaque manche : plus de cartes (+1), plus de deniers (+1), le 7 de deniers « Settebello » (+1), meilleure Primiera (+1) et +1 par Scopa.',
    'Primiera : ta meilleure carte par couleur, 7=21, 6=18, As=16, 5=15, 4=14, 3=13, 2=12, figures=10. Une couleur manquante la fait perdre.',
    'Le premier au score cible avec une avance gagne.',
  ],
  briscolaRules: [
    'La Briscola se joue avec un jeu italien de 40 cartes : Deniers, Coupes, Épées et Bâtons.',
    'Chaque joueur reçoit 3 cartes. Une carte est retournée face visible sous la pioche : sa couleur indique la Briscola (atout) et sera la dernière carte piochée.',
    'Le premier joueur entame avec la carte de son choix. Le second peut jouer n’importe quelle carte : il n’y a aucune obligation de fournir à la couleur ni de couper.',
    'Dans une même couleur, l’ordre de force est : As (1), Trois (3), Roi (10), Cavalier (9), Valet (8), 7, 6, 5, 4, 2.',
    'Si le second joue une Briscola sur une autre couleur, il remporte le pli. Si les deux cartes sont de même couleur, la plus forte l’emporte ; sinon la première carte tient.',
    'Le gagnant du pli ramasse les deux cartes, pioche en premier et entame le pli suivant.',
    'Le jeu compte 120 points au total : As = 11 pts, Trois = 10 pts, Roi (10) = 4 pts, Cavalier (9) = 3 pts, Valet (8) = 2 pts. Les autres cartes valent 0 pt.',
    'Il faut 61 points ou plus pour remporter la manche (60–60 est une égalité).',
  ],
};

const pt: Partial = {
  tagline: 'O clássico jogo de cartas italiano',
  briscolaTagline: 'O clássico jogo italiano de trunfo e vazas',
  gameMode: 'Jogo',
  playPc: 'Jogar contra o PC',
  playFriend: 'Jogar com um amigo',
  settings: 'Definições',
  howToPlay: 'Como jogar',
  back: 'Voltar',
  start: 'Começar',
  difficulty: 'Dificuldade',
  EASY: 'Principiante',
  MEDIUM: 'Experiente',
  HARD: 'Mestre',
  EASY_desc: 'Descontraído. Erra e não conta cartas.',
  MEDIUM_desc: 'Pensa em cada captura e descarta com cuidado.',
  HARD_desc: 'Conta todas as cartas e joga o final na perfeição.',
  EASY_briscola_desc: 'Joga de forma descontraída e gasta trunfos cedo.',
  MEDIUM_briscola_desc: 'Protege os Ases e Três e corta no momento certo.',
  HARD_briscola_desc: 'Conta todas as cartas e resolve as últimas vazas na perfeição.',
  targetScore: 'Pontuação alvo',
  matchFormat: 'Formato da partida',
  standard: 'Normal',
  long: 'Longa',
  singleGame: 'Único',
  bestOf3: 'Melhor de 3',
  bestOf5: 'Melhor de 5',
  turnTimer: 'Tempo por jogada',
  none: 'Nenhum',
  pileInspector: 'Ver capturas',
  pileInspectorDesc: 'Os jogadores podem ver as próprias capturas (casual).',
  createLink: 'Criar link de convite',
  inviteTitle: 'Convidar um amigo',
  inviteText: 'Envia este link. O jogo começa assim que o abrir.',
  share: 'Partilhar',
  copy: 'Copiar link',
  copied: 'Copiado!',
  waitingFor: 'À espera do teu amigo…',
  joinTitle: 'Foste convidado para Scopa',
  joinTitleBriscola: 'Foste convidado para Briscola',
  joinAs: 'O teu nome',
  join: 'Entrar',
  roomNotFound: 'Este link expirou ou não existe.',
  roomFull: 'Este jogo já tem dois jogadores.',
  connecting: 'A ligar…',
  reconnecting: 'A religar…',
  offline: 'Servidor indisponível. Verifica a ligação.',
  you: 'Tu',
  computer: 'Computador',
  deck: 'Baralho',
  pile: 'Capturas',
  cards: 'cartas',
  yourTurn: 'É a tua vez',
  theirTurn: '{name} está a pensar…',
  chooseCapture: 'Escolhe as cartas a capturar',
  cancel: 'Cancelar',
  lastTake: 'A última captura leva a mesa',
  settebelloYours: 'O 7 de ouros é seu! +1',
  settebelloTheirs: '{name} levou o 7 de ouros!',
  roundOver: 'Ronda {n}',
  carte: 'Cartas',
  denari: 'Ouros',
  trump: 'Trunfo',
  suit_DENARI: 'Ouros',
  suit_COPPE: 'Copas',
  suit_SPADE: 'Espadas',
  suit_BASTONI: 'Paus',
  assi: 'Ases (11)',
  tre: 'Três (10)',
  figure: 'Figuras (4/3/2)',
  puntiMano: 'Pontos da mão',
  handsWon: 'Mãos ganhas',
  drawRound: 'Empate (60–60)!',
  total: 'Total',
  continue: 'Continuar',
  waitingOpp: 'À espera do adversário…',
  youWin: 'Ganhaste!',
  youLose: '{name} ganha',
  forfeitWin: '{name} saiu — ganhaste.',
  forfeitLose: 'Perdeste por desistência.',
  rematch: 'Desforra',
  rematchWaiting: 'À espera da desforra…',
  oppWantsRematch: '{name} quer a desforra!',
  home: 'Início',
  quit: 'Sair do jogo',
  quitConfirm: 'Sair do jogo?',
  paused: '{name} desligou-se. A aguardar {s}s…',
  timedOut: 'Tempo esgotado — uma carta foi jogada automaticamente',
  language: 'Idioma',
  deckStyle: 'Baralho',
  showNumbers: 'Mostrar números',
  sound: 'Efeitos sonoros',
  music: 'Música',
  haptics: 'Vibração',
  emotesMuted: 'Silenciar emotes',
  name: 'O teu nome',
  locked: 'Bloqueado',
  unlockBergamasche: 'Ganha 3 jogos para desbloquear',
  unlockTrevisane: 'Vence o Mestre para desbloquear',
  unlocked: 'Novo baralho desbloqueado: {deck}!',
  stats: 'Estatísticas',
  wins: 'Vitórias',
  losses: 'Derrotas',
  emote_hello: 'Olá!',
  emote_gg: 'Bom jogo!',
  myPile: 'As minhas capturas',
  coinsCount: 'Ouros',
  yes: 'Sim',
  no: 'Não',
  rotate: 'Roda o dispositivo para vertical',
  nextTrack: 'Próxima música',
  watchIntro: 'Ver vídeo de introdução',
  introVideoDesc: 'Apresentação rápida do jogo e das regras',
  close: 'Fechar',
  newRound: 'Ronda {n} — {name} dá as cartas',
  rules: [
    'Joga-se com um baralho italiano de 40 cartas: Ouros, Copas, Espadas e Paus, do Ás ao 7 mais Valete (8), Cavalo (9) e Rei (10).',
    'Cada jogador recebe 3 cartas e 4 ficam viradas na mesa. Quando as mãos acabam, dão-se mais 3 até o baralho acabar.',
    'Na tua vez jogas uma carta. Se houver uma carta do mesmo valor tens de a capturar; senão tens de capturar cartas cuja soma seja igual, se houver.',
    'Se houver uma carta individual igual, tens de capturar essa e não uma soma.',
    'Se não puderes capturar, a carta fica na mesa.',
    'Limpar a mesa é Scopa: +1 ponto (exceto na última jogada da ronda).',
    'No fim, as cartas que sobram vão para quem capturou por último.',
    'Cada ronda: mais cartas (+1), mais ouros (+1), o 7 de ouros "Settebello" (+1), melhor Primiera (+1) e +1 por Scopa.',
    'Primiera: a melhor carta de cada naipe, 7=21, 6=18, Ás=16, 5=15, 4=14, 3=13, 2=12, figuras=10. Sem um naipe perdes.',
    'Ganha quem chega ao alvo com vantagem.',
  ],
  briscolaRules: [
    'A Briscola joga-se com um baralho italiano de 40 cartas: Ouros, Copas, Espadas e Paus.',
    'Cada jogador recebe 3 cartas. Uma carta fica virada para cima sob o baralho: o seu naipe define a Briscola (trunfo) e é a última carta a ser comprada.',
    'O primeiro a jogar lança qualquer carta. O segundo pode jogar qualquer carta — não há obrigação de assistir ao naipe nem de cortar com trunfo.',
    'Dentro do mesmo naipe, a força das cartas da maior para a menor é: Ás (1), Três (3), Rei (10), Cavalo (9), Valete (8), 7, 6, 5, 4, 2.',
    'Se o segundo jogar Briscola contra outro naipe, ganha a vaza. Se ambos jogarem o mesmo naipe, ganha a carta mais forte; caso contrário, vence a primeira carta.',
    'Quem ganha a vaza recolhe ambas as cartas, compra primeiro do baralho e sai para a vaza seguinte.',
    'O baralho tem 120 pontos no total: Ás = 11 pts, Três = 10 pts, Rei (10) = 4 pts, Cavalo (9) = 3 pts, Valete (8) = 2 pts. As restantes cartas valem 0 pts.',
    'Ganha a mão quem somar 61 ou mais pontos (60–60 é empate).',
  ],
};

const TABLES: Record<Lang, Partial> = { en, it, es, fr, pt };

let current: Lang = 'en';

export function detectLang(): Lang {
  const nav = (globalThis.navigator?.language ?? 'en').slice(0, 2).toLowerCase();
  return (LANGS as readonly string[]).includes(nav) ? (nav as Lang) : 'en';
}

export function setLang(l: Lang) {
  current = l;
  document.documentElement.lang = l;
}

export const getLang = () => current;

type StringKey = { [K in keyof Strings]: Strings[K] extends string ? K : never }[keyof Strings];

export function t(key: StringKey, vars?: Record<string, string | number>): string {
  let s = (TABLES[current][key] ?? en[key]) as string;
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

export const rulesText = (game: GameType = 'scopa'): string[] =>
  game === 'briscola' ? (TABLES[current].briscolaRules ?? en.briscolaRules) : (TABLES[current].rules ?? en.rules);

